/** Bandeja de aprobaciones móvil (mismo contrato workflow que la web). */
import { mobileApiClient, newIdempotencyKey } from './mobileApiClient';

export type WorkflowTaskStatus =
  | 'open'
  | 'unavailable'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'reassigned';

export type WorkflowExecutionKind =
  | 'domain_command'
  | 'workflow_request'
  | 'workflow_decision'
  | 'workflow_control';

export type WorkflowActionVariant = 'primary' | 'success' | 'warning' | 'danger';

export type WorkflowAssigneeSnapshot = {
  display_name: string;
  membership_id?: string;
  user_id?: string;
};

/** Lectura real de GET /api/v1/workflow-tasks/mine. */
export type WorkflowTask = {
  id: string;
  tenant_id: string;
  instance_id: string;
  instance_step_id: string;
  assigned_user_id: string;
  assigned_at: string;
  due_at: string | null;
  status: WorkflowTaskStatus;
  revision: number;
  assignee_snapshot: WorkflowAssigneeSnapshot;
  document_code?: string | null;
  step_name?: string | null;
  closed_at?: string | null;
};

export type WorkflowTaskInstanceSummary = {
  id: string;
  entity_code: string;
  entity_id: string;
  workflow_point_code: string;
  status?: string;
  revision?: number;
  document_code?: string | null;
};

export type WorkflowTaskDocumentSummary = {
  label: string;
  entity_code: string;
  entity_id: string;
  business_status: string;
  document_revision: number;
};

export type WorkflowTaskAvailableAction = {
  action_code: string;
  execution_kind?: WorkflowExecutionKind;
  label: string;
  icon?: string;
  variant?: WorkflowActionVariant;
  sort_order?: number;
  workflow_point_code?: string | null;
  task_id?: string | null;
  requires_reason?: boolean;
  data_fields?: WorkflowActionDataField[];
  enabled?: boolean;
  disabled_code?: string | null;
  disabled_reason?: string | null;
  required_role_names?: string[];
};

export type WorkflowActionFieldOption = {
  value: string;
  label: string;
};

export type WorkflowActionDataField = {
  name: string;
  label: string;
  required: boolean;
  options: WorkflowActionFieldOption[];
};

/** Lectura real de GET /api/v1/workflow-tasks/{id}. */
export type WorkflowTaskDetail = {
  task: WorkflowTask;
  task_revision: number;
  instance_summary: WorkflowTaskInstanceSummary;
  document_summary: WorkflowTaskDocumentSummary;
  snapshot: unknown;
  available_actions: WorkflowTaskAvailableAction[];
  route_version_number?: number | null;
  definition_version_number?: number | null;
  selected_rule_name?: string | null;
};

/** Acción disponible del contexto de flujo (WorkflowAvailableActionRead). */
export type WorkflowContextAction = {
  code: string;
  action_code?: string;
  execution_kind?: WorkflowExecutionKind;
  label: string;
  icon?: string;
  variant?: WorkflowActionVariant;
  sort_order?: number;
  requires_reason: boolean;
  enabled?: boolean;
  disabled?: boolean;
  disabled_code?: string | null;
  disabled_reason?: string | null;
  workflow_point_code?: string | null;
  task_id?: string | null;
  data_fields: WorkflowActionDataField[];
  required_role_names: string[];
};

function normalizeActionField(raw: Record<string, unknown>): WorkflowActionDataField {
  const options = Array.isArray(raw['options'])
    ? (raw['options'] as Record<string, unknown>[])
        .map((option) => ({
          value:
            typeof option['value'] === 'string'
              ? (option['value'] as string)
              : String(option['value'] ?? ''),
          label:
            typeof option['label'] === 'string'
              ? (option['label'] as string)
              : String(option['value'] ?? ''),
        }))
        .filter((option) => option.value !== '')
    : [];
  const name =
    typeof raw['name'] === 'string'
      ? (raw['name'] as string)
      : typeof raw['field'] === 'string'
        ? (raw['field'] as string)
        : '';
  return {
    name,
    label: typeof raw['label'] === 'string' ? (raw['label'] as string) : name,
    required: raw['required'] === true,
    options,
  };
}

export function normalizeContextAction(raw: Record<string, unknown>): WorkflowContextAction {
  const code =
    typeof raw['action_code'] === 'string'
      ? (raw['action_code'] as string)
      : typeof raw['code'] === 'string'
        ? (raw['code'] as string)
        : '';
  const enabled = typeof raw['enabled'] === 'boolean' ? (raw['enabled'] as boolean) : undefined;
  const disabled =
    typeof raw['disabled'] === 'boolean'
      ? (raw['disabled'] as boolean)
      : enabled === undefined
        ? undefined
        : !enabled;
  const executionKind =
    raw['execution_kind'] === 'domain_command' ||
    raw['execution_kind'] === 'workflow_request' ||
    raw['execution_kind'] === 'workflow_decision' ||
    raw['execution_kind'] === 'workflow_control'
      ? (raw['execution_kind'] as WorkflowExecutionKind)
      : undefined;
  const variant =
    raw['variant'] === 'primary' ||
    raw['variant'] === 'success' ||
    raw['variant'] === 'warning' ||
    raw['variant'] === 'danger'
      ? (raw['variant'] as WorkflowActionVariant)
      : undefined;
  const dataFields = Array.isArray(raw['data_fields'])
    ? (raw['data_fields'] as Record<string, unknown>[])
        .map(normalizeActionField)
        .filter((field) => field.name !== '')
    : [];

  return {
    code,
    action_code: code || undefined,
    execution_kind: executionKind,
    label: typeof raw['label'] === 'string' ? (raw['label'] as string) : code,
    icon: typeof raw['icon'] === 'string' ? (raw['icon'] as string) : undefined,
    variant,
    sort_order: typeof raw['sort_order'] === 'number' ? (raw['sort_order'] as number) : undefined,
    requires_reason: raw['requires_reason'] === true,
    enabled,
    disabled,
    disabled_code: (raw['disabled_code'] as string | null) ?? null,
    disabled_reason: (raw['disabled_reason'] as string | null) ?? null,
    workflow_point_code: (raw['workflow_point_code'] as string | null) ?? null,
    task_id:
      raw['task_id'] === null || raw['task_id'] === undefined ? null : String(raw['task_id']),
    data_fields: dataFields,
    required_role_names: Array.isArray(raw['required_role_names'])
      ? (raw['required_role_names'] as unknown[]).filter(
          (role): role is string => typeof role === 'string',
        )
      : [],
  };
}

/** Punto del recorrido del documento (WorkflowPointRead). */
export type WorkflowContextPoint = {
  code: string;
  label: string;
};

function normalizeContextPoint(raw: Record<string, unknown>): WorkflowContextPoint {
  const code =
    typeof raw['workflow_point_code'] === 'string'
      ? (raw['workflow_point_code'] as string)
      : typeof raw['code'] === 'string'
        ? (raw['code'] as string)
        : '';
  return {
    code,
    label: typeof raw['label'] === 'string' ? (raw['label'] as string) : code,
  };
}

/** Instancia del expediente con su revisión para retirar o reanudar. */
export type WorkflowContextInstance = {
  id: string;
  entity_code: string;
  entity_id: string;
  workflow_point_code: string;
  status: string;
  revision: number;
  document_code?: string | null;
};

function normalizeContextInstance(raw: Record<string, unknown>): WorkflowContextInstance {
  return {
    id: String(raw['id'] ?? ''),
    entity_code: typeof raw['entity_code'] === 'string' ? (raw['entity_code'] as string) : '',
    entity_id: String(raw['entity_id'] ?? ''),
    workflow_point_code:
      typeof raw['workflow_point_code'] === 'string'
        ? (raw['workflow_point_code'] as string)
        : '',
    status: typeof raw['status'] === 'string' ? (raw['status'] as string) : '',
    revision:
      typeof raw['revision'] === 'number'
        ? (raw['revision'] as number)
        : Number(raw['revision'] ?? 0) || 0,
    document_code:
      raw['document_code'] === null || raw['document_code'] === undefined
        ? null
        : String(raw['document_code']),
  };
}

export type WorkflowContext = {
  actions: WorkflowContextAction[];
  points: WorkflowContextPoint[];
  latest_instances: WorkflowContextInstance[];
  document_revision: number;
  business_status?: string;
  entity_code?: string;
  entity_id?: string;
};

export function normalizeContext(raw: Record<string, unknown>): WorkflowContext {
  const actions = Array.isArray(raw['actions'])
    ? (raw['actions'] as Record<string, unknown>[]).map(normalizeContextAction)
    : [];
  const points = Array.isArray(raw['points'])
    ? (raw['points'] as Record<string, unknown>[]).map(normalizeContextPoint)
    : [];
  const latestInstances = Array.isArray(raw['latest_instances'])
    ? (raw['latest_instances'] as Record<string, unknown>[]).map(normalizeContextInstance)
    : [];
  const revision = raw['document_revision'];
  return {
    actions,
    points,
    latest_instances: latestInstances,
    document_revision: typeof revision === 'number' ? revision : Number(revision ?? 0) || 0,
    business_status:
      typeof raw['business_status'] === 'string' ? (raw['business_status'] as string) : undefined,
    entity_code: typeof raw['entity_code'] === 'string' ? (raw['entity_code'] as string) : undefined,
    entity_id:
      raw['entity_id'] === null || raw['entity_id'] === undefined
        ? undefined
        : String(raw['entity_id']),
  };
}

export type WorkflowOperation = {
  operation_status: 'applied' | 'blocked' | 'invalidated';
  instance_id: string;
  instance_status: string;
  domain_status: string;
  current_step_id: string | null;
  revision: number;
  errors: unknown[];
  replayed: boolean;
};

export type WorkflowMembershipLookup = {
  membership_id: string;
  user_id: string;
  display_name: string;
  status: string;
  eligible: boolean;
  unavailable_reason: string | null;
};

export type DecideTaskInput = {
  decision: 'approve' | 'reject';
  reason?: string;
  expectedTaskRevision: number;
};

export type WorkflowRequestInput = {
  entityCode: string;
  entityId: string;
  workflowPointCode: string;
  expectedDocumentRevision: number;
};

export type WorkflowControlInput = {
  reason: string;
  expectedInstanceRevision: number;
};

export type WorkflowReassignInput = {
  membershipId: string;
  reason: string;
  expectedTaskRevision: number;
};

export type WorkflowTaskFilters = {
  status?: 'open';
  status_group?: 'resolved';
};

export const tasksService = {
  async mine(filters: WorkflowTaskFilters = {}): Promise<WorkflowTask[]> {
    const { data } = await mobileApiClient.get('/api/v1/workflow-tasks/mine', {
      params: {
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.status_group ? { status_group: filters.status_group } : {}),
      },
    });
    return (data.items ?? data) as WorkflowTask[];
  },
  async get(taskId: string): Promise<WorkflowTaskDetail> {
    const { data } = await mobileApiClient.get(`/api/v1/workflow-tasks/${taskId}`);
    return data as WorkflowTaskDetail;
  },
  async context(entityCode: string, entityId: string): Promise<WorkflowContext> {
    const { data } = await mobileApiClient.get(
      `/api/v1/workflow-context/${entityCode}/${entityId}`,
    );
    return normalizeContext(data as Record<string, unknown>);
  },
  async getInstance(instanceId: string): Promise<Record<string, unknown>> {
    const { data } = await mobileApiClient.get(`/api/v1/workflow-instances/${instanceId}`);
    return data as Record<string, unknown>;
  },
  async requestWorkflow(input: WorkflowRequestInput): Promise<WorkflowOperation> {
    const { data } = await mobileApiClient.post(
      '/api/v1/workflow-instances',
      {
        entity_code: input.entityCode,
        entity_id: input.entityId,
        workflow_point_code: input.workflowPointCode,
        expected_document_revision: input.expectedDocumentRevision,
      },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data as WorkflowOperation;
  },
  async decide(taskId: string, input: DecideTaskInput): Promise<WorkflowOperation> {
    const { data } = await mobileApiClient.post(
      `/api/v1/workflow-tasks/${taskId}/decision`,
      {
        decision: input.decision,
        reason: input.reason,
        expected_task_revision: input.expectedTaskRevision,
      },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data as WorkflowOperation;
  },
  async reassign(taskId: string, input: WorkflowReassignInput): Promise<WorkflowOperation> {
    const { data } = await mobileApiClient.post(
      `/api/v1/workflow-tasks/${taskId}/reassign`,
      {
        membership_id: input.membershipId,
        reason: input.reason,
        expected_task_revision: input.expectedTaskRevision,
      },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data as WorkflowOperation;
  },
  async withdraw(instanceId: string, input: WorkflowControlInput): Promise<WorkflowOperation> {
    const { data } = await mobileApiClient.post(
      `/api/v1/workflow-instances/${instanceId}/withdraw`,
      {
        reason: input.reason,
        expected_instance_revision: input.expectedInstanceRevision,
      },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data as WorkflowOperation;
  },
  async resume(instanceId: string, input: WorkflowControlInput): Promise<WorkflowOperation> {
    const { data } = await mobileApiClient.post(
      `/api/v1/workflow-instances/${instanceId}/resume`,
      {
        reason: input.reason,
        expected_instance_revision: input.expectedInstanceRevision,
      },
      { headers: { 'Idempotency-Key': newIdempotencyKey() } },
    );
    return data as WorkflowOperation;
  },
  async memberships(input: {
    entityCode: string;
    workflowPointCode: string;
    taskId: string;
    search?: string;
  }): Promise<WorkflowMembershipLookup[]> {
    const { data } = await mobileApiClient.get('/api/v1/workflow-lookups/memberships', {
      params: {
        entity_code: input.entityCode,
        workflow_point_code: input.workflowPointCode,
        purpose: 'reassignment',
        task_id: input.taskId,
        page_size: 100,
        ...(input.search ? { q: input.search } : {}),
      },
    });
    const items = (data as { items?: WorkflowMembershipLookup[] }).items;
    return items ?? [];
  },
};
